<template>
  <ProSpaceVLayout class="adjustment-block" :gap="7">
    <ProSpaceHLayout justifyContent="space-between" align-items="flex-start" :gap="20">
      <ProSpaceLabel
        :text="editedField.name"
        bold
        hint
      />
      <ProSpaceTag
        :text="statusText"
        :type="getStatusType()"
      />
    </ProSpaceHLayout>
    <ProSpaceHLayout :gap="20" align-items="flex-start">
      <ProSpaceTextBlock
        type="secondary"
        :top="$t('adjustmentOldValue')"
        :bottom="getFieldOldValue(editedField)"
        :title="getFieldOldValue(editedField)"
        :gap="2"
        class="adjustment-block__text-block"
      />
      <ProSpaceTextBlock
        type="secondary"
        :top="$t('adjustmentNewValue')"
        :title="getFieldNewValue(editedField)"
        :bottom="getFieldNewValue(editedField)"
        :gap="2"
        class="adjustment-block__text-block"
      />
    </ProSpaceHLayout>
    <ProSpaceHLayout :gap="10" v-if="showSelectBtns">
      <ProSpaceSelectButton
        class="adjustments-grid__select-btn"
        v-model="editedField[currentStatus]"
        :items="[
          {value: 'false', label: this.$t('adjustmentBlockDecline'), icon: 'close'},
          {value: 'true', label: this.$t('adjustmentBlockAccept'), icon: 'check'}
        ]"
      />
    </ProSpaceHLayout>
  </ProSpaceVLayout>
</template>
<script>
import {
  ProSpaceTag,
  ProSpaceLabel,
  ProSpaceAction,
  ProSpaceTextBlock,
  ProSpaceHLayout,
  ProSpaceVLayout,
  ProSpaceSelectButton
} from "@prospace/prospace-components-library";
import formatDigit from "@prospace/prospace-components-library/src/lib-assets/mixins/digitFormatter";

export default {
  components: {
    ProSpaceTag,
    ProSpaceLabel,
    ProSpaceAction,
    ProSpaceTextBlock,
    ProSpaceHLayout,
    ProSpaceVLayout,
    ProSpaceSelectButton
  },
  props: {
    editedField: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false
    },
    checkAccess: {
      type: Function
    },
    currentStatus: {
      type: String
    },
    adjustmentStatus: {
      type: String
    }
  },
  data() {
    return {
      oldEditedField: {},
      percentageKeys: ['osaPlan', 'deltaISAOSAPlan'],
      formattingKeys: [
        "ratePerHourPlan",
        "ratePerHourFact",
        "timingPerVisitPlan",
        "timingPerVisitFact",
        "fteMerchPlan",
        "fteMerchFact",
        "osaPlan",
        "osaFact",
        "deltaISAOSAPlan",
        "deltaISAOSAFact",
        "visitSumPlan",
        "osaSumPlan",
        "totalSumPlan",
        "visitSumFact",
        "osaSumFact",
        "totalSumFact",
        "osaTargetPlanW",
        "osaTargetFactW",
        "osaReportPlanW",
        "osaReportFactW",
        "pogWetWHSPlan",
        "pogWetPFTPlan",
        "pogWetSHBPlan",
        "pogDryWHSPlan",
        "pogDryPFTPlan",
        "lSizePlan",
        "stripeCTDreamPlan",
        "stripePFTCatPlan",
        "stripePFDogPlan",
        "stripeSPPlan",
        "ctShareDreamPlan",
        "ctSharePFCatPlan",
        "ctSharePFDogPlan",
        "exSitePlan",
        "hotZonePlan"
      ]
    };
  },
  watch: {
    editedField: {
      handler(val) {
        this.oldEditedField = val
      },
      immediate: true
    }
  },
  methods: {
    formatDigit,
    accept() {
      this.editedField.status = "adjustmentFieldAccepted";
    },
    decline() {
      this.editedField.status = "adjustmentFieldDeclined";
    },
    cancel() {
      this.editedField.status = "adjustmentFieldRequested";
    },
    getStatusType() {
      let status
      if (this.adjustmentStatus === 'onapproval') {
        status = this.oldEditedField['acceptanceStatus']
      } else if (this.adjustmentStatus === 'completed') {
        status = this.oldEditedField['approvalStatus']
      } else if (this.adjustmentStatus === 'deleted') {
        status = this.oldEditedField['acceptanceStatus']
      }

      let type = "default"
      switch (status) {
        case "true":
        case true:
          type = "success"
          break;
        case "false":
        case false:
          type = "error"
          break;
      }

      return type;
    },
    getFieldOldValue(data) {
      let value = data.oldDisplayValue || data.oldValue;
      if (data.key == 'coverage') {
        value = this.$t(`coverage_${data.oldValue.toString().toLowerCase()}`);
      }
      if (!isNaN(value) && String(value).includes('.')) {
        value = parseFloat(value).toFixed(2);
      } else if (!isNaN(value.replace(',', '.')) && String(value.replace(',', '.')).includes('.')) {
        value = parseFloat(value.replace(',', '.')).toFixed(2);
      }
      if (this.formattingKeys.includes(data.key)) {
        value = formatDigit(value);
      }
      if (!isNaN(value) && this.percentageKeys.includes(data.key)) {
        value += '%';
      }
      return value;
    },
    getFieldNewValue(data) {
      let value = data.displayValue || data.newValue;
      if (data.key == 'coverage') {
        value = this.$t(`coverage_${data.newValue}`);
      }
      if (!isNaN(value) && String(value).includes('.')) {
        value = parseFloat(value).toFixed(2);
      }
      if (this.formattingKeys.includes(data.key)) {
        value = formatDigit(value);
      }
      if (!isNaN(value) && this.percentageKeys.includes(data.key)) {
        value += '%';
      }
      return value;
    }
  },
  computed: {
    isProceeded() {
      return this.editedField.status !== "adjustmentFieldRequested";
    },
    statusText() {
      if (this.adjustmentStatus === 'onacceptance') {
        return this.$t('adjustmentFieldRequested')
      }
      if (this.adjustmentStatus === 'onapproval') {
        return this.oldEditedField['acceptanceStatus'] === 'true' || this.oldEditedField['acceptanceStatus'] === true ? this.$t('adjustmentFieldAccepted') : this.$t('adjustmentFieldDeclined')
      }
      if (this.adjustmentStatus === 'completed') {
        return this.oldEditedField['approvalStatus'] === 'true' || this.oldEditedField['approvalStatus'] === true ? this.$t('adjustmentFieldAccepted') : this.$t('adjustmentFieldDeclined')
      }
      if (this.adjustmentStatus === 'deleted') {
        if (this.oldEditedField['acceptanceStatus'] === null) {
          return this.$t('adjustmentFieldRequested')
        }
        return this.oldEditedField['acceptanceStatus'] === 'true' || this.oldEditedField['acceptanceStatus'] === true ? this.$t('adjustmentFieldAccepted') : this.$t('adjustmentFieldDeclined')
      }
    },
    showSelectBtns() {
      if (this.adjustmentStatus === 'completed') return false;
      if (this.adjustmentStatus === 'onacceptance' && this.checkAccess('adjustments-acceptance')) return true
      else if (this.adjustmentStatus === 'onapproval' && this.checkAccess('adjustments-approval')) return true
    }
  },
};
</script>
<style lang="scss" scoped>
.adjustment-block {
  width: calc(100% - 5px); // to show left/right box-shadow
  margin: 0 auto;
  padding: 10px;
  border-radius: 5px;
  box-shadow: 0px 2px 10px rgba(51, 51, 51, 0.07);
  :deep(.adjustment-block__text-block) {
    width: calc(50% - 10px);
  }
  :deep(.adjustment-block__text-block .prospace-text-block__top) {
    font-size: 11px;
    font-weight: 400;
  }
  :deep(.adjustment-block__text-block .prospace-text-block__bottom) {
    font-size: 12px;
  }
  :deep(.adjustment-block__actions .prospace-action-text) {
    font-size: 11px;
  }
  :deep(.adjustment-block__actions .prospace-action > div) {
    gap: 5px !important;
  }
}
</style>
